import { createAudioCatalog, parseAudioManifest, type AudioCatalog, type MusicRole } from '../../../packages/audio/src/index.ts';
import { acousticDay, soundscape, sourceMix, surfaceAt, type SoundPoint, type Soundscape } from './soundscape.ts';

type Bus = 'music' | 'effects' | 'voice';
/** Interface sounds on the effects bus: one clip per action. */
export const UI_SOUNDS = {
  hover: { id: 'sfx:ui:hover', gain: .05, gapMs: 90 },
  press: { id: 'sfx:ui:tick', gain: .12 },
  'codex-open': { id: 'sfx:foley:parchment-unroll' },
  'codex-tab': { id: 'sfx:ui:page' },
  'codex-close': { id: 'sfx:dialogue:close' },
  'menu-open': { id: 'sfx:dialogue:open' },
  'menu-close': { id: 'sfx:dialogue:close' },
  seal: { id: 'sfx:ui:seal' },
  reveal: { id: 'sfx:ui:reveal' },
} satisfies Record<string, { id: string; gain?: number; gapMs?: number }>;
export interface SoundWalker { id: string; at: SoundPoint; moving: boolean; dead: boolean; gear?: string | undefined; player: boolean }
export interface PlayAudio {
  readonly catalog: AudioCatalog;
  readonly musicTitle: string;
  readonly description: string;
  snapshot(): object;
  startMenuTheme(): void;
  enterScene(zone: string): void;
  scene(place: string, beat: string, at: SoundPoint, delta: number, held: boolean, walkers: readonly SoundWalker[]): void;
  sfx(id: string, at?: SoundPoint): void;
  ui(action: keyof typeof UI_SOUNDS): void;
  combat(active: boolean): void;
  resolveCombat(): void;
  voice(textId: string): { readonly subtitles: readonly string[]; readonly durationS: number };
  volume(bus: Bus | 'master', value: number): void;
  theme(role: MusicRole): void;
  cue(state: string, ambience?: boolean): void;
  mute(value: boolean): void;
  toggleMute(): boolean;
  stop(): void;
}
interface Playing {
  id: string; element: HTMLAudioElement; bus: Bus; gain: number; target: number; bed: boolean;
  retiring: boolean; pan?: StereoPannerNode; filter?: BiquadFilterNode; source?: MediaElementAudioSourceNode;
}

export async function loadPlayAudio(base = ''): Promise<PlayAudio> {
  const catalog = createAudioCatalog(parseAudioManifest(await (await fetch(`${base}assets/manifests/audio-slice.json`)).json()));
  const live = new Set<Playing>(), ambience = new Map<string, Playing>(), speech = new Set<Playing>();
  const volumes = { master: 1, music: .7, effects: .8, voice: 1 };
  const lastSfx = new Map<string, number>();
  const walkers = new Map<string, { at: SoundPoint; distance: number; step: number; last: number; sound?: Playing }>();
  let muted = false, unlocked = false, context: AudioContext | undefined, timer: number | undefined;
  let currentZone: string | undefined, music: Playing | undefined, desiredMusic: ReturnType<AudioCatalog['music']>[number] | undefined;
  let mode: 'menu' | 'explore' | 'combat' | 'story' = 'menu', keepAmbience = true;
  let placeKey = '', place: Soundscape | undefined, listener: SoundPoint = [0, 0], elapsed = 0, updateAt = 0, nextPhrase = 3;
  let description = '', day = acousticDay(0);
  const effective = (value: Playing): number => muted || document.hidden ? 0 : Math.max(0, Math.min(1,
    value.gain * volumes.master * volumes[value.bus] * (value.bed && speech.size ? .25 : 1)));
  function mix(): void {
    for (const value of live) { value.element.volume = effective(value); value.element.muted = muted || document.hidden; }
  }
  function remove(value: Playing): void {
    value.element.pause(); value.element.removeAttribute('src'); value.element.load();
    value.source?.disconnect(); value.filter?.disconnect(); value.pan?.disconnect();
    live.delete(value); speech.delete(value);
    for (const [id, playing] of ambience) if (playing === value) ambience.delete(id);
    if (music === value) music = undefined;
    mix();
  }
  function fade(value: Playing): void { value.target = 0; value.retiring = true; }
  function pulse(): void {
    for (const value of live) {
      if (value.bed) value.gain += (value.target - value.gain) * .095;
      if (value.retiring && value.gain < .001) remove(value);
    }
    mix();
  }
  function play(id: string, path: string, gain: number, bus: Bus, loop = false, bed = false, spatial = false): Playing | undefined {
    if (!unlocked) return;
    if (timer === undefined) timer = window.setInterval(pulse, 50);
    // Streaming keeps long music and ambience out of decoded-buffer memory on phones.
    const element = new Audio(`${base}${path}`); element.loop = loop; element.preload = 'auto';
    const value: Playing = { id, element, bus, gain: bed ? 0 : gain, target: gain, bed, retiring: false };
    if (context && spatial) {
      value.source = context.createMediaElementSource(element); value.filter = context.createBiquadFilter(); value.filter.type = 'lowpass';
      value.filter.frequency.value = 15000; value.filter.Q.value = .5; value.pan = context.createStereoPanner();
      value.source.connect(value.filter).connect(value.pan).connect(context.destination);
    }
    live.add(value);
    element.addEventListener('ended', () => { if (music === value && mode === 'explore') nextPhrase = elapsed + 32; remove(value); }, { once: true });
    element.addEventListener('error', () => remove(value), { once: true });
    mix(); void element.play().catch(() => remove(value)); return value;
  }
  function spatial(value: Playing, at: SoundPoint, radius = 360): void {
    const mix = sourceMix({ cue: '', caption: '', gain: 1, at, radius }, listener, day.weights);
    value.pan?.pan.setTargetAtTime(mix.pan, context!.currentTime, .15);
    value.filter?.frequency.setTargetAtTime(mix.cutoff, context!.currentTime, .2);
  }
  function startMusic(cue: ReturnType<AudioCatalog['music']>[number] | undefined): void {
    desiredMusic = cue;
    if (!unlocked || !cue || music?.id === cue.id && !music.retiring) return;
    // At most two score streams survive a transition, even while clicking through menus.
    for (const value of live) if (value.bus === 'music' && value !== music) remove(value);
    if (music) fade(music);
    music = play(cue.id, cue.audio.path, cue.defaultGain, 'music', mode === 'explore' ? false : cue.loop, true);
  }
  function switchMusic(role: MusicRole): void {
    startMusic(catalog.music(role, currentZone).find(cue => cue.zone === currentZone) ?? catalog.music(role)[0]);
  }
  function explore(): void {
    mode = 'explore'; keepAmbience = true; desiredMusic = undefined;
    if (music) fade(music);
    nextPhrase = elapsed + 3;
  }
  function beds(): void {
    const wanted = new Set<string>();
    for (const layer of keepAmbience ? place?.layers ?? [] : []) {
      wanted.add(layer.cue);
      const mix = sourceMix(layer, listener, day.weights);
      let value = ambience.get(layer.cue);
      if (!value) {
        const cue = catalog.manifest.sfx.find(cue => cue.id === layer.cue);
        if (cue) value = play(cue.id, cue.audio.path, mix.gain, 'effects', true, true, true);
        if (value) ambience.set(layer.cue, value);
      }
      if (value) {
        value.target = mix.gain; value.retiring = false;
        value.pan?.pan.setTargetAtTime(mix.pan, context!.currentTime, .2);
        value.filter?.frequency.setTargetAtTime(mix.cutoff, context!.currentTime, .3);
      }
    }
    const retiring = [...ambience].filter(([id]) => !wanted.has(id)).map(([, value]) => value);
    for (const value of retiring) fade(value);
    // Rapid travel cannot accumulate abandoned loops. Four current + four outgoing maximum.
    for (const value of retiring.slice(0, Math.max(0, ambience.size - 8))) remove(value);
  }
  function sfx(id: string, gain?: number, key = id, gapMs = 120, at?: SoundPoint): Playing | undefined {
    if (!unlocked) return;
    const now = performance.now();
    if (now - (lastSfx.get(key) ?? -Infinity) < gapMs) return;
    const cue = catalog.manifest.sfx.find(cue => cue.id === id); if (!cue) return;
    lastSfx.set(key, now);
    const effects = [...live].filter(value => value.bus === 'effects' && !value.bed);
    if (effects.length >= 16) remove(effects[0]!);
    const near = at ? sourceMix({ cue: id, caption: '', gain: 1, at, radius: 430 }, listener, day.weights).gain : 1;
    const value = play(id, cue.audio.path, (gain ?? cue.defaultGain ?? .3) * near, 'effects', false, false, !!at);
    if (value && at) spatial(value, at, 430);
    return value;
  }
  function unlock(event: Event): void {
    if (unlocked || !event.isTrusted || !navigator.userActivation.isActive) return;
    unlocked = true;
    document.removeEventListener('pointerdown', unlock, true); document.removeEventListener('pointerup', unlock, true); document.removeEventListener('keydown', unlock, true);
    if (typeof AudioContext !== 'undefined') { context = new AudioContext(); void context.resume().catch(() => {}); }
    if (desiredMusic) startMusic(desiredMusic);
    beds();
  }
  document.addEventListener('pointerdown', unlock, true); document.addEventListener('pointerup', unlock, true); document.addEventListener('keydown', unlock, true);
  document.addEventListener('visibilitychange', mix);
  return {
    catalog, musicTitle: catalog.music('menu')[0]?.title ?? '',
    get description() { return description; },
    snapshot() { return { unlocked, muted, volumes: { ...volumes }, phase: day.phase, hour: day.hour, place: placeKey, mode,
      context: context?.state ?? 'not-created', music: music?.id ?? null, description,
      streams: [...live].map(value => ({ id: value.id, bus: value.bus, gain: value.gain, target: value.target, effective: effective(value),
        pan: value.pan?.pan.value ?? 0, cutoff: value.filter?.frequency.value ?? 15000, retiring: value.retiring, paused: value.element.paused, time: value.element.currentTime })) }; },
    startMenuTheme() { mode = 'menu'; switchMusic('menu'); },
    enterScene(zone) { currentZone = zone; if (mode !== 'explore') explore(); },
    scene(id, beat, at, delta, held, figures) {
      listener = at;
      if (!held && !document.hidden) elapsed += delta / 1000;
      day = acousticDay(elapsed);
      const next = soundscape(id, beat);
      if (beat !== placeKey) { walkers.clear(); placeKey = beat; updateAt = 0; }
      if (next !== place) { const changedTheme = next?.theme !== place?.theme; place = next; if (mode === 'explore' && changedTheme) explore(); updateAt = 0; }
      if (performance.now() >= updateAt) {
        updateAt = performance.now() + 100;
        beds();
        const prominent = [...place?.layers ?? []].sort((a, b) => sourceMix(b, at, day.weights).gain - sourceMix(a, at, day.weights).gain).slice(0, 2);
        description = `${day.phase} · ${prominent.map(layer => layer.caption).join('. ')}.`;
        if (mode === 'explore' && !held && !music && elapsed >= nextPhrase) {
          startMusic(catalog.musicState(`faction-${place?.theme}`) ?? catalog.music('exploration', currentZone).find(cue => cue.zone === currentZone));
          // A failed file gets a bounded retry, never a new stream every frame.
          nextPhrase = elapsed + 40;
        }
      }
      const present = new Set(figures.filter(figure => !figure.dead).map(figure => figure.id));
      for (const [id, previous] of walkers) if (!present.has(id)) { if (previous.sound && live.has(previous.sound)) remove(previous.sound); walkers.delete(id); }
      for (const figure of figures) {
        if (figure.dead) continue;
        let previous = walkers.get(figure.id);
        if (!previous) { previous = { at: figure.at, distance: 0, step: 0, last: -Infinity }; walkers.set(figure.id, previous); }
        const distance = Math.hypot(figure.at[0] - previous.at[0], figure.at[1] - previous.at[1]); previous.at = figure.at;
        if (held || !figure.moving || distance > 120) { previous.distance = 0; continue; }
        previous.distance += distance;
        if (previous.distance < 26 || performance.now() - previous.last < 260) continue;
        previous.distance %= 26; previous.last = performance.now(); previous.step++;
        if (previous.sound && live.has(previous.sound)) remove(previous.sound);
        const surface = surfaceAt(place, figure.at);
        const value = sfx(`sfx:footstep:${surface}`, figure.player ? .27 : .13, `step:${figure.id}`, 0, figure.at);
        if (value) { value.element.preservesPitch = false; value.element.playbackRate = previous.step % 2 ? .96 : 1.04; previous.sound = value; }
        if (figure.gear && previous.step % 4 === 0) sfx(`sfx:gear:${figure.gear}`, .16, `gear:${figure.id}`, 1200, figure.at);
      }
    },
    sfx(id, at) { sfx(id, undefined, id, 120, at); },
    ui(action) { const sound: { id: string; gain?: number; gapMs?: number } = UI_SOUNDS[action]; sfx(sound.id, sound.gain, `ui:${action}`, sound.gapMs); },
    combat(active) { if (active) { mode = 'combat'; switchMusic('combat'); } else explore(); },
    resolveCombat() { explore(); nextPhrase = elapsed + 18; },
    voice(textId) {
      for (const value of speech) remove(value);
      const cues = catalog.cuesForText(textId), cue = cues.find(cue => cue.audio);
      if (cue?.audio) { const value = play(cue.textId, cue.audio.path, .58, 'voice'); if (value) { speech.add(value); mix(); } }
      return { subtitles: cues.map(cue => cue.subtitle), durationS: cue?.audio?.durationS ?? 0 };
    },
    theme(role) { mode = 'story'; keepAmbience = false; beds(); switchMusic(role); },
    cue(state, ambience = true) { mode = 'story'; keepAmbience = ambience; beds(); startMusic(catalog.musicState(state)); },
    mute(value) { muted = value; mix(); },
    volume(bus, value) { if (Number.isFinite(value)) volumes[bus] = Math.max(0, Math.min(1, value)); mix(); },
    toggleMute() { muted = !muted; mix(); return muted; },
    stop() {
      window.clearInterval(timer); timer = undefined;
      for (const value of live) remove(value);
      music = undefined; desiredMusic = undefined; place = undefined; placeKey = ''; description = ''; walkers.clear(); lastSfx.clear();
      elapsed = 0; nextPhrase = 3;
    },
  };
}
